import React, { useEffect, useState } from 'react';
import {
  CCard,
  CCardBody,
  CCol,
  CNav,
  CNavItem,
  CNavLink,
  CRow,
  CTabContent,
  CTabPane,
  CTabs,
} from '@coreui/react';
import { useLocation } from 'react-router-dom';
import PageLayout from '../../components/PageLayout';
import _t from 'counterpart';
import CaptchaIntegration from './CaptchaIntegration';
import EmailIntegration from './EmailIntegration';
import VoipIntegration from './VoipIntegration';
import TwilioIntegration from './TwilioIntegration';
import WalletConfiguration from './WalletConfiguration';
import SumSubIntegration from './SumSubIntegration';
import RolloverTimeSettings from './RolloverTime';
import CustomerDataMaskingSettings from './CustomerDataMaskingSettings';
import DefaultCustomerSecurityLevel from './DefaultCustomerSecurityLevel';
import MetaPixelIntegration from './MetaPixelIntegration';
import PendingWithdrawalDeduction from './PendingWithdrawalDeduction';
import CreditRemovalSetting from './CreditRemovalSetting';
import CreditValiditySetting from './CreditValiditySetting';
import RolePermissionsSettings from './RolePermissionsSettings';
import { useAppSelector } from '../../helpers/customHooks';
import { Permission } from '../../reducers/userReducer';

const TAB_INTEGRATIONS = 'integrations';
const TAB_PLATFORM = 'platform';
const TAB_CREDITS = 'credits';
const TAB_SECURITY = 'security';

const GeneralSettingsPage = () => {
  const location = useLocation();
  const permissions = useAppSelector((state) => state.user.permissions);
  const hasManageCustomerSecurityLevelPermission = permissions?.includes(Permission.MANAGE_CUSTOMER_SECURITY_LEVEL);
  const hasManageRolePermissionsPermission = permissions?.includes(Permission.MANAGE_ROLE_PERMISSIONS);
  const showSecurityTab = hasManageRolePermissionsPermission || hasManageCustomerSecurityLevelPermission;

  const [activeTab, setActiveTab] = useState<string>(TAB_INTEGRATIONS);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  useEffect(() => {
    const raw = location.hash.replace(/^#/, '');
    if (raw === 'email') {
      setActiveTab(TAB_INTEGRATIONS);
    }
  }, [location.hash]);

  useEffect(() => {
    if (!showSecurityTab && activeTab === TAB_SECURITY) {
      setActiveTab(TAB_INTEGRATIONS);
    }
  }, [showSecurityTab, activeTab]);

  return (
    <PageLayout title={_t('sidebar.general-settings')}>
      <CRow>
        <CCol>
          <CTabs activeTab={activeTab} onActiveTabChange={handleTabChange}>
            <CNav variant="tabs">
              <CNavItem>
                <CNavLink data-tab={TAB_INTEGRATIONS}>{_t('generalSettings.tabs.integrations')}</CNavLink>
              </CNavItem>
              <CNavItem>
                <CNavLink data-tab={TAB_PLATFORM}>{_t('generalSettings.tabs.platform')}</CNavLink>
              </CNavItem>
              <CNavItem>
                <CNavLink data-tab={TAB_CREDITS}>{_t('generalSettings.tabs.credits')}</CNavLink>
              </CNavItem>
              {showSecurityTab && (
                <CNavItem>
                  <CNavLink data-tab={TAB_SECURITY}>{_t('generalSettings.tabs.security')}</CNavLink>
                </CNavItem>
              )}
            </CNav>
            <CCard className="card--with-top-tabs">
              <CCardBody>
                <CTabContent>
                  <CTabPane data-tab={TAB_INTEGRATIONS} className="no-border-top">
                    <VoipIntegration />
                    <EmailIntegration />
                    <CaptchaIntegration />
                    <TwilioIntegration />
                    <MetaPixelIntegration />
                    <SumSubIntegration />
                  </CTabPane>
                  <CTabPane data-tab={TAB_PLATFORM} className="no-border-top">
                    <WalletConfiguration />
                    <RolloverTimeSettings />
                    <PendingWithdrawalDeduction />
                  </CTabPane>
                  <CTabPane data-tab={TAB_CREDITS} className="no-border-top">
                    <CreditRemovalSetting />
                    <CreditValiditySetting />
                  </CTabPane>
                  {showSecurityTab && (
                    <CTabPane data-tab={TAB_SECURITY} className="no-border-top">
                      {hasManageRolePermissionsPermission && <RolePermissionsSettings />}
                      {hasManageCustomerSecurityLevelPermission && (
                        <>
                          <CustomerDataMaskingSettings />
                          <DefaultCustomerSecurityLevel />
                        </>
                      )}
                    </CTabPane>
                  )}
                </CTabContent>
              </CCardBody>
            </CCard>
          </CTabs>
        </CCol>
      </CRow>
    </PageLayout>
  );
};

export default GeneralSettingsPage;
